import React, { useState, useEffect } from 'react';
import api from '../api';
import { Receipt, CalendarBlank, CurrencyDollar, Eye, Clock } from '@phosphor-icons/react';
import { Badge } from './ui/badge';

const ClientFinanceiro = ({ clientId }) => {
  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [clientId]);

  const fetchData = async () => {
    try {
      const [clientsRes, paymentsRes] = await Promise.all([
        api.get('/clients'),
        api.get(`/pagamentos-clientes?client_id=${clientId}`)
      ]);
      const c = clientsRes.data.find(cl => cl.id === clientId);
      setClient(c);
      setPayments(paymentsRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-400">Carregando...</div>;
  if (!client) return <div className="text-center py-8 text-zinc-400">Cliente nao encontrado</div>;

  const totalPago = payments.reduce((acc, p) => acc + (p.valor || 0), 0);

  const vigenciaInicio = client.data_inicio_vigencia ? new Date(client.data_inicio_vigencia + 'T00:00:00') : null;
  const vigenciaFim = client.data_fim_vigencia ? new Date(client.data_fim_vigencia + 'T00:00:00') : null;
  const hoje = new Date();
  const diasRestantes = vigenciaFim ? Math.max(0, Math.ceil((vigenciaFim - hoje) / (1000 * 60 * 60 * 24))) : 0;
  const vigenciaAtiva = vigenciaFim ? hoje <= vigenciaFim : false;

  const proximoVencimento = client.dia_vencimento ? (() => {
    const d = new Date();
    d.setDate(client.dia_vencimento);
    if (d < hoje) d.setMonth(d.getMonth() + 1);
    return d;
  })() : null;

  return (
    <div className="space-y-6">
      {/* Vigência do Plano */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="vigencia-section">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          Vigencia do Plano
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
            <div className="text-zinc-500 text-xs mb-1">Plano Contratado</div>
            <div className="text-[#FFB800] font-bold text-lg">{client.plano_contratado || 'N/A'}</div>
          </div>
          <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
            <div className="text-zinc-500 text-xs mb-1">Periodo de Vigencia</div>
            <div className="text-white text-sm">
              {vigenciaInicio ? vigenciaInicio.toLocaleDateString('pt-BR') : 'N/A'}
              {' - '}
              {vigenciaFim ? vigenciaFim.toLocaleDateString('pt-BR') : 'N/A'}
            </div>
          </div>
          <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
            <div className="text-zinc-500 text-xs mb-1">Dias Restantes</div>
            <div className={`font-bold text-lg ${diasRestantes <= 30 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>
              {diasRestantes} dias
            </div>
          </div>
          <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
            <div className="text-zinc-500 text-xs mb-1">Status</div>
            <Badge className={vigenciaAtiva ? 'bg-[#22C55E]' : 'bg-red-500'}>
              {vigenciaAtiva ? 'Ativo' : 'Expirado'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Resumo Financeiro */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="total-pago">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
            <CurrencyDollar size={16} />
            Total Pago
          </div>
          <div className="text-3xl font-bold text-[#22C55E]" style={{ fontFamily: 'Barlow Condensed' }}>
            R$ {totalPago.toFixed(2)}
          </div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="pagamentos-count">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
            <Receipt size={16} />
            Pagamentos Realizados
          </div>
          <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            {payments.length}
          </div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="proximo-vencimento">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
            <CalendarBlank size={16} />
            Proximo Vencimento
          </div>
          <div className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            {proximoVencimento ? `Dia ${client.dia_vencimento} - ${proximoVencimento.toLocaleDateString('pt-BR')}` : 'N/A'}
          </div>
        </div>
      </div>

      {/* Horas do Plano */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          Horas do Plano
        </h3>
        {/* Eletricista */}
        <div className="mb-4 p-4 bg-[#050505] border border-[#27272A] rounded-sm">
          <div className="text-zinc-400 text-sm mb-2 flex items-center gap-2">Horas Eletricista</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-zinc-500 text-xs">Contratadas</div>
              <div className="text-white font-bold text-xl">{(client.horas_eletricista_contratadas || 0).toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-zinc-500 text-xs">Utilizadas</div>
              <div className="text-white font-bold text-xl">{(client.horas_eletricista_usadas || 0).toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-zinc-500 text-xs">Disponiveis</div>
              <div className={`font-bold text-xl ${((client.horas_eletricista_contratadas || 0) - (client.horas_eletricista_usadas || 0)) < 3 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>
                {((client.horas_eletricista_contratadas || 0) - (client.horas_eletricista_usadas || 0)).toFixed(1)}h
              </div>
            </div>
          </div>
        </div>
        {/* Auxiliar */}
        <div className="mb-4 p-4 bg-[#050505] border border-[#27272A] rounded-sm">
          <div className="text-zinc-400 text-sm mb-2 flex items-center gap-2">Horas Auxiliar</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-zinc-500 text-xs">Contratadas</div>
              <div className="text-white font-bold text-xl">{(client.horas_auxiliar_contratadas || 0).toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-zinc-500 text-xs">Utilizadas</div>
              <div className="text-white font-bold text-xl">{(client.horas_auxiliar_usadas || 0).toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-zinc-500 text-xs">Disponiveis</div>
              <div className={`font-bold text-xl ${((client.horas_auxiliar_contratadas || 0) - (client.horas_auxiliar_usadas || 0)) < 3 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>
                {((client.horas_auxiliar_contratadas || 0) - (client.horas_auxiliar_usadas || 0)).toFixed(1)}h
              </div>
            </div>
          </div>
        </div>
        {/* Total */}
        <div className="p-4 bg-[#050505] border border-[#FFB800]/30 rounded-sm">
          <div className="text-[#FFB800] text-sm mb-2 font-semibold">Total (Eletricista + Auxiliar)</div>
          <div className="grid grid-cols-3 gap-4">
            <div><div className="text-zinc-500 text-xs">Contratadas</div><div className="text-white font-bold text-2xl">{client.contracted_hours?.toFixed(1) || 0}h</div></div>
            <div><div className="text-zinc-500 text-xs">Utilizadas</div><div className="text-white font-bold text-2xl">{client.used_hours?.toFixed(1) || 0}h</div></div>
            <div><div className="text-zinc-500 text-xs">Disponiveis</div><div className={`font-bold text-2xl ${(client.contracted_hours - client.used_hours) < 5 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>{(client.contracted_hours - client.used_hours).toFixed(1)}h</div></div>
          </div>
        </div>
      </div>

      {/* Lista de Pagamentos */}
      <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="payments-list">
        <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          Historico de Pagamentos
        </h3>
        {payments.length === 0 ? (
          <div className="text-zinc-500 text-center py-8">Nenhum pagamento registrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#27272A]">
                  <th className="text-left text-zinc-400 py-3 px-3 font-medium">Data</th>
                  <th className="text-left text-zinc-400 py-3 px-3 font-medium">Descricao</th>
                  <th className="text-right text-zinc-400 py-3 px-3 font-medium">Valor</th>
                  <th className="text-center text-zinc-400 py-3 px-3 font-medium">Comprovante</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-b border-[#27272A]/50 hover:bg-[#1E1E1E]/50" data-testid={`client-payment-${p.id}`}>
                    <td className="py-3 px-3 text-white">{new Date(p.data + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                    <td className="py-3 px-3 text-zinc-300">{p.descricao}</td>
                    <td className="py-3 px-3 text-[#22C55E] font-bold text-right">R$ {p.valor?.toFixed(2)}</td>
                    <td className="py-3 px-3 text-center">
                      {p.comprovante_url ? (
                        <a href={p.comprovante_url} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:text-[#E6A600] inline-flex items-center gap-1 text-xs">
                          <Eye size={14} />Ver
                        </a>
                      ) : <span className="text-zinc-600 text-xs">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientFinanceiro;
